// Cache a single DOMParser instance to avoid per-call allocations
let cachedDOMParser: DOMParser | null = null

const getDOMParser = (): DOMParser | null => {
  // Guard for non-browser environments
  if (typeof DOMParser === "undefined") return null
  if (!cachedDOMParser) cachedDOMParser = new DOMParser()
  return cachedDOMParser
}

const BLOCK_TAGS = [
  "p",
  "div",
  "br",
  "li",
  "section",
  "article",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6"
]

export const htmlToText = (
  html: string,
  {
    separator = " ",
    ignoreTags = []
  }: { separator?: string; ignoreTags?: string[] } = {}
): string => {
  const parser = getDOMParser()
  const ignoreTagsLower = ignoreTags?.map((t) => t.toLowerCase()) ?? []

  if (!parser) {
    // Fallback: lightweight extraction without DOMParser
    // 1) Insert separators for block tags (excluding ignored ones)
    const effectiveBlockTags = BLOCK_TAGS.filter(
      (t) => !ignoreTagsLower.includes(t)
    )

    let interim = html
    effectiveBlockTags.forEach((tag) => {
      const tagRe = new RegExp(`<\\/?${tag}(?:\\s[^>]*)?>`, "gi")
      interim = interim.replace(tagRe, separator)
    })

    // 2) Strip remaining tags
    interim = interim.replace(/<[^>]*>/g, "")

    // 3) Normalize whitespace later using the same logic as below
    const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")
    const sepRegex = escapeRegex(separator)

    let text = interim
    text = text.replace(new RegExp(`^(?:\\s*${sepRegex})+`), "")
    text = text.replace(new RegExp(`(?:${sepRegex}){2,}`, "g"), separator)
    text = text.replace(new RegExp(`(?:\\s*${sepRegex}\\s*){2,}`, "g"), separator)
    return text.trim()
  }

  const doc = parser.parseFromString(html, "text/html")

  // Replace block-level elements with spaces for now
  // This results in a single line of text, but handle that in rendering
  // Using line breaks creates awkward spacing in the search results
  BLOCK_TAGS.forEach((tag) => {
    const elements = doc.body.querySelectorAll(tag)
    elements.forEach((el) => {
      // Normalize tag-name comparisons to be case-insensitive
      const elTag = el.tagName.toLowerCase()
      const shouldIgnore = ignoreTagsLower.includes(elTag)
      if (shouldIgnore) {
        return
      }
      if (el.parentNode) {
        el.insertAdjacentText("beforebegin", separator ?? " ")
      }
    })
  })

  // Build final string and normalize separators:
  // - Remove separators at the very start
  // - Collapse duplicate separators (e.g., ". . ") into a single instance
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const sepRegex = escapeRegex(separator)

  let text = doc.body.textContent ?? ""
  // Remove leading separators (with optional surrounding whitespace)
  text = text.replace(new RegExp(`^(?:\\s*${sepRegex})+`), "")
  // Collapse consecutive separators into a single separator
  text = text.replace(new RegExp(`(?:${sepRegex}){2,}`, "g"), separator)
  // Also collapse cases with accidental spaces around repeated separators
  text = text.replace(new RegExp(`(?:\\s*${sepRegex}\\s*){2,}`, "g"), separator)

  return text.trim()
}
